import type { FC, PropsWithChildren } from 'react';

import type { BoxProps, TextProps } from '@opendoor/bricks-next';

import { PressableBox } from 'platform/bricks-next';

import { HOMEPAGE_URL } from 'shared/helpers/globals';
import { OpendoorDashboardLogo, OpendoorMobileNavLogo } from 'shared/helpers/svgCollection';

type LogoVariant = 'primary' | 'secondary';

type HomepageLinkProps = {
  variant: LogoVariant;
};

const HomepageLink: FC<PropsWithChildren<HomepageLinkProps>> = ({ children, variant }) => {
  const isPrimary = variant === 'primary';
  const analyticsName = isPrimary ? 'opendoor-full-logo' : 'opendoor-o-logo';

  return (
    <PressableBox
      href={HOMEPAGE_URL}
      analyticsName={analyticsName}
      aria-label="go to the Opendoor homepage"
    >
      {children}
    </PressableBox>
  );
};

type LogoProps = {
  height?: BoxProps['height'];
  width?: BoxProps['width'];

  /**
   * The fill color for the logo.
   * Defaults to brand blue.
   */
  color?: TextProps['color'];

  /**
   * The logo variant to display:
   *  - primary: Opendoor spelled out
   *  - secondary: A large letter O
   * Defaults to primary.
   */
  variant?: 'primary' | 'secondary';
};

export const Logo: FC<LogoProps> = ({
  width,
  height,
  color = '$contentAccentBrand',
  variant = 'primary',
}) =>
  variant === 'primary' ? (
    <OpendoorDashboardLogo width={width} height={height} color={color} />
  ) : (
    <OpendoorMobileNavLogo width={width} height={height} color={color} />
  );

/**
 * Shows a small Opendoor logo that navigates to the homepage when clicked
 */
export const LogoLink: FC<LogoProps> = ({
  width,
  height,
  color = '$contentAccentBrand',
  variant = 'primary',
}) => (
  <HomepageLink variant={variant}>
    <Logo width={width} height={height} color={color} variant={variant} />
  </HomepageLink>
);
